/**
 * PL-Pflege-Sicht: Eigene Kunden in einer Kampagne als 2D-Matrix
 * (Kunden × Zielsegmente, T10).
 *
 * Schnellpflege via Dropdown direkt in Tabellenzellen, Bulk-Aktion
 * "Alle ausgewählten Paare auf 'Vorgestellt: Geplant' setzen".
 * Hat eine Kampagne nur ein Zielsegment, fällt die Matrix optisch auf
 * eine Spalte zusammen und liest sich wie die bisherige Liste.
 */
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { Link } from "@tanstack/react-router";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "../../hooks/useAuth.js";
import {
  useCampaign,
  useParticipations,
  useUpdateParticipation,
  useBulkUpdateParticipations,
  type ParticipationItem,
} from "../../hooks/useCampaigns.js";
import { useSegments } from "../../hooks/useSegments.js";

// ── Labels ───────────────────────────────────────────────────────────────────

function useRelevantLabel() {
  const { t } = useTranslation();
  return (v: string): string => {
    if (v === "yes" || v === "no" || v === "not_assessed") {
      return t(`participationStatus.relevant.${v}`);
    }
    return v;
  };
}

function usePresentedLabel() {
  const { t } = useTranslation();
  return (v: string): string => {
    if (v === "yes" || v === "no" || v === "not_done" || v === "planned") {
      return t(`participationStatus.presented.${v}`);
    }
    return v;
  };
}

function useInstalledLabel() {
  const { t } = useTranslation();
  return (v: string): string => {
    if (v === "yes" || v === "no" || v === "not_done" || v === "in_progress") {
      return t(`participationStatus.installed.${v}`);
    }
    return v;
  };
}

// ── MembersPage ──────────────────────────────────────────────────────────────

interface MembersPageProps {
  campaignId: string;
  campaignTitle?: string;
}

export function CampaignMembersPage({ campaignId, campaignTitle }: MembersPageProps) {
  const { t } = useTranslation();
  const { user: _user } = useCurrentUser();
  const [page, setPage] = useState(1);
  // Auswahl erfolgt pro participation.id (= Kunde × Segment).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: campaign } = useCampaign(campaignId);
  const { data: segments } = useSegments();
  const {
    data: participationsData,
    isLoading,
    isError,
  } = useParticipations(campaignId, { page, pageSize: 100 });

  const bulkMutation = useBulkUpdateParticipations(campaignId);

  const items = useMemo(
    () => participationsData?.items ?? [],
    [participationsData],
  );
  const allIds = useMemo(() => new Set(items.map((i) => i.id)), [items]);

  // Spaltendefinition: Zielsegmente der Kampagne, in Anzeige-Reihenfolge der
  // segments-Tabelle (segments.displayOrder), gefiltert auf das, was die
  // Kampagne tatsächlich anpeilt. Wenn nur eines übrig bleibt, ist die Matrix
  // optisch eine einzelne Spalte (single-segment Sonderfall).
  const segmentColumns = useMemo(() => {
    if (!campaign || !segments) return [] as Array<{ id: string; name: string }>;
    const targetIds = new Set(campaign.targetSegments.map((s) => s.id));
    return segments
      .filter((s) => targetIds.has(s.id))
      .map((s) => ({ id: s.id, name: s.name }));
  }, [campaign, segments]);

  // Kunden → (segmentId → Item)-Map; alphabetisch sortiert.
  const rows = useMemo(() => {
    const byCustomer = new Map<
      string,
      {
        customerId: string;
        customerName: string;
        cells: Map<string, ParticipationItem>;
      }
    >();
    for (const it of items) {
      const entry = byCustomer.get(it.customerId) ?? {
        customerId: it.customerId,
        customerName: it.customerName,
        cells: new Map<string, ParticipationItem>(),
      };
      entry.cells.set(it.segmentId, it);
      byCustomer.set(it.customerId, entry);
    }
    return [...byCustomer.values()].sort((a, b) =>
      a.customerName.localeCompare(b.customerName, "de"),
    );
  }, [items]);

  function toggleAll() {
    if (selectedIds.size === allIds.size) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  }

  /** Wählt/entwählt alle Paare eines Kunden auf einmal (eine Zeile). */
  function toggleRow(customerId: string) {
    const row = rows.find((r) => r.customerId === customerId);
    if (!row) return;
    const rowIds = [...row.cells.values()].map((c) => c.id);
    const allSelected = rowIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of rowIds) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  function handleBulkSetPresentedPlanned() {
    const ids = selectedIds.size > 0 ? [...selectedIds] : items.map((i) => i.id);
    void bulkMutation.mutateAsync({
      participationIds: ids,
      patch: { presented: "planned" },
    });
  }

  const isMultiSegment = segmentColumns.length > 1;

  return (
    <Stack gap="md">
      {/* Rückweg + Kontext: ohne Kampagnennamen weiß niemand, worin er pflegt
          (Deep-Link/Refresh). Der Name kommt aus der ohnehin geladenen Kampagne. */}
      <Anchor
        component={Link}
        to="/campaigns/$id"
        params={{ id: campaignId } as never}
        size="sm"
        c="dimmed"
      >
        ← {campaign?.title ?? t("pages.campaigns.title")}
      </Anchor>
      <Title order={3}>
        {t("pages.campaigns.membersPageTitle")}
        {campaign?.title || campaignTitle
          ? `: ${campaign?.title ?? campaignTitle}`
          : ""}
      </Title>

      {isError && (
        <Alert color="red" title={t("common.error")}>
          {t("pages.campaigns.membersErrorLoading")}
        </Alert>
      )}

      {/* Bulk-Aktionen */}
      <Group gap="sm">
        <Button
          size="sm"
          variant="light"
          onClick={handleBulkSetPresentedPlanned}
          loading={bulkMutation.isPending}
          disabled={isLoading || items.length === 0}
        >
          {selectedIds.size > 0
            ? t("pages.campaigns.membersBulkSelected", { count: selectedIds.size })
            : t("pages.campaigns.membersBulkAll")}
        </Button>
        {bulkMutation.isError && (
          <Text size="sm" c="red">
            {t("pages.campaigns.membersBulkError")}
            {bulkMutation.error instanceof Error && bulkMutation.error.message
              ? `: ${bulkMutation.error.message}`
              : ""}
          </Text>
        )}
      </Group>

      {/* Tabelle / Matrix */}
      {isLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} height={44} radius="sm" />
          ))}
        </Stack>
      ) : rows.length === 0 ? (
        <Text c="dimmed" ta="center" py="md">
          {t("pages.campaigns.detail.noMembers")}
        </Text>
      ) : (
        <Box style={{ overflowX: "auto" }}>
          <Table withTableBorder withColumnBorders highlightOnHover>
            <Table.Thead>
              {isMultiSegment ? (
                <>
                  <Table.Tr>
                    <Table.Th rowSpan={2} w={40}>
                      <Checkbox
                        checked={selectedIds.size === allIds.size && allIds.size > 0}
                        indeterminate={selectedIds.size > 0 && selectedIds.size < allIds.size}
                        onChange={toggleAll}
                        size="sm"
                      />
                    </Table.Th>
                    <Table.Th rowSpan={2}>{t("pages.campaigns.detail.tableCustomer")}</Table.Th>
                    <Table.Th rowSpan={2}>{t("pages.campaigns.detail.tableVia")}</Table.Th>
                    {segmentColumns.map((s) => (
                      <Table.Th key={s.id} colSpan={3} ta="center">
                        {s.name}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                  <Table.Tr>
                    {segmentColumns.map((s) => (
                      <React.Fragment key={`${s.id}-sub`}>
                        <Table.Th>{t("pages.campaigns.detail.tableRelevant")}</Table.Th>
                        <Table.Th>{t("pages.campaigns.detail.tablePresented")}</Table.Th>
                        <Table.Th>{t("pages.campaigns.detail.tableInstalled")}</Table.Th>
                      </React.Fragment>
                    ))}
                  </Table.Tr>
                </>
              ) : (
                <Table.Tr>
                  <Table.Th w={40}>
                    <Checkbox
                      checked={selectedIds.size === allIds.size && allIds.size > 0}
                      indeterminate={selectedIds.size > 0 && selectedIds.size < allIds.size}
                      onChange={toggleAll}
                      size="sm"
                    />
                  </Table.Th>
                  <Table.Th>{t("pages.campaigns.detail.tableCustomer")}</Table.Th>
                  <Table.Th>{t("pages.campaigns.detail.tableVia")}</Table.Th>
                  <Table.Th>{t("pages.campaigns.detail.tableRelevant")}</Table.Th>
                  <Table.Th>{t("pages.campaigns.detail.tablePresented")}</Table.Th>
                  <Table.Th>{t("pages.campaigns.detail.tableInstalled")}</Table.Th>
                </Table.Tr>
              )}
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => {
                const rowIds = [...row.cells.values()].map((c) => c.id);
                const allSelected =
                  rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));
                const someSelected = rowIds.some((id) => selectedIds.has(id));
                const firstCell = [...row.cells.values()][0];
                return (
                  <Table.Tr key={row.customerId}>
                    <Table.Td>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={() => toggleRow(row.customerId)}
                        size="sm"
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {row.customerName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {firstCell && (
                        <Badge
                          size="xs"
                          color={firstCell.addType === "filter" ? "blue" : "gray"}
                          variant="light"
                        >
                          {firstCell.addType === "filter"
                            ? t("pages.campaigns.detail.viaFilterLabel")
                            : t("pages.campaigns.detail.viaManualLabel")}
                        </Badge>
                      )}
                    </Table.Td>
                    {segmentColumns.map((s) => {
                      const cell = row.cells.get(s.id);
                      if (!cell) {
                        return (
                          <Table.Td key={s.id} colSpan={3}>
                            <Text size="xs" c="dimmed" ta="center">
                              —
                            </Text>
                          </Table.Td>
                        );
                      }
                      return (
                        <MembersCellGroup
                          key={s.id}
                          campaignId={campaignId}
                          item={cell}
                        />
                      );
                    })}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      {/* Pagination */}
      {(participationsData?.total ?? 0) > (participationsData?.pageSize ?? 100) && (
        <Group justify="center">
          <Button
            size="xs"
            variant="subtle"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("common.previousPage")}
          </Button>
          <Text size="sm">
            {t("common.pageOf", {
              page,
              total: Math.ceil(
                (participationsData?.total ?? 0) / (participationsData?.pageSize ?? 100),
              ),
            })}
          </Text>
          <Button
            size="xs"
            variant="subtle"
            disabled={
              page >=
              Math.ceil((participationsData?.total ?? 0) / (participationsData?.pageSize ?? 100))
            }
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.nextPage")}
          </Button>
        </Group>
      )}
    </Stack>
  );
}

// ── MembersCellGroup ─────────────────────────────────────────────────────────
//
// Drei Selects pro (Kunde × Segment)-Paar. Wird als Fragment in eine Zeile
// der Matrix gerendert.

function MembersCellGroup({
  campaignId,
  item,
}: {
  campaignId: string;
  item: ParticipationItem;
}) {
  const relevantLabel = useRelevantLabel();
  const presentedLabel = usePresentedLabel();
  const installedLabel = useInstalledLabel();
  const updateMutation = useUpdateParticipation(campaignId, item.id);

  function update(patch: Parameters<typeof updateMutation.mutate>[0]) {
    updateMutation.mutate(patch);
  }

  return (
    <>
      <Table.Td>
        <Select
          size="xs"
          data={[
            { value: "not_assessed", label: relevantLabel("not_assessed") },
            { value: "yes", label: relevantLabel("yes") },
            { value: "no", label: relevantLabel("no") },
          ]}
          value={item.relevant}
          onChange={(v) => {
            if (v) update({ relevant: v as ParticipationItem["relevant"] });
          }}
          style={{ minWidth: 130 }}
        />
      </Table.Td>
      <Table.Td>
        <Select
          size="xs"
          data={[
            { value: "not_done", label: presentedLabel("not_done") },
            { value: "planned", label: presentedLabel("planned") },
            { value: "yes", label: presentedLabel("yes") },
            { value: "no", label: presentedLabel("no") },
          ]}
          value={item.presented}
          onChange={(v) => {
            if (v) update({ presented: v as ParticipationItem["presented"] });
          }}
          style={{ minWidth: 130 }}
          disabled={item.relevant === "no"}
        />
      </Table.Td>
      <Table.Td>
        <Select
          size="xs"
          data={[
            { value: "not_done", label: installedLabel("not_done") },
            { value: "in_progress", label: installedLabel("in_progress") },
            { value: "yes", label: installedLabel("yes") },
            { value: "no", label: installedLabel("no") },
          ]}
          value={item.installed}
          onChange={(v) => {
            if (v) update({ installed: v as ParticipationItem["installed"] });
          }}
          style={{ minWidth: 130 }}
          disabled={item.relevant === "no" || item.presented !== "yes"}
        />
      </Table.Td>
    </>
  );
}
